/**
 * Health Trends Dashboard Component
 * Displays wellness scores, metrics, and symptom trends with charts
 */
import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, Activity, Heart, 
  Scale, Moon, Thermometer, Droplets, Calendar,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = {
  primary: '#8b5cf6',
  secondary: '#ec4899',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  mild: '#22c55e',
  moderate: '#f59e0b',
  severe: '#ef4444'
};

const PIE_COLORS = ['#8b5cf6', '#ec4899', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444'];

// Time range options
const TIME_RANGES = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 }
];

const HealthTrendsDashboard = ({ token, isPreview = false, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [trendsData, setTrendsData] = useState(null);
  const [selectedRange, setSelectedRange] = useState(30);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, wellness, metrics, symptoms

  useEffect(() => {
    if (token) {
      fetchTrendsData();
    }
  }, [token, selectedRange]);

  const fetchTrendsData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/trends?days=${selectedRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrendsData(data);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCustomRange = () => {
    if (customDateRange.start && customDateRange.end) {
      const start = new Date(customDateRange.start);
      const end = new Date(customDateRange.end);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (days > 0 && days <= 365) {
        setSelectedRange(days);
        setShowCustomRange(false);
      }
    }
  };

  // Preview Card Component (for home page)
  if (isPreview) {
    return (
      <div 
        onClick={onClose}
        className="bg-gradient-to-br from-indigo-600/30 to-purple-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-indigo-500/30"
        data-testid="health-trends-card"
      >
        <Activity className="w-12 h-12 text-indigo-400 mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Health Trends</h3>
        <p className="text-gray-400 text-sm mb-4">
          View wellness scores, metrics & symptom trends over time
        </p>
        {trendsData?.wellness && (
          <div className="flex items-center gap-2">
            <div className="flex items-center text-indigo-400">
              <span className="text-2xl font-bold">{trendsData.wellness.average_score}</span>
              <span className="text-sm ml-1">/10</span>
            </div>
            {trendsData.wellness.change !== 0 && (
              <span className={`flex items-center text-xs ${trendsData.wellness.change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {trendsData.wellness.change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {Math.abs(trendsData.wellness.change).toFixed(1)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full Dashboard View
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={onClose}
              className="text-white flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-purple-400" />
              Health Trends
            </h1>
            <div className="w-20"></div>
          </div>
          
          {/* Time Range Selector */}
          <div className="flex items-center gap-2 flex-wrap">
            {TIME_RANGES.map(range => (
              <button
                key={range.value}
                onClick={() => setSelectedRange(range.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedRange === range.value && !showCustomRange
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                }`}
              >
                {range.label}
              </button>
            ))}
            <button
              onClick={() => setShowCustomRange(!showCustomRange)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                showCustomRange ? 'bg-purple-500 text-white' : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              <Calendar className="w-4 h-4" /> Custom
            </button>
          </div>
          
          {/* Custom Date Range Picker */}
          {showCustomRange && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm"
              />
              <button
                onClick={applyCustomRange}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium"
              >
                Apply
              </button>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {['overview', 'wellness', 'metrics', 'symptoms'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab
                    ? 'bg-slate-700 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : !trendsData ? (
          <div className="text-center py-20 text-gray-400">
            No health data available. Start tracking your health to see trends!
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard
                    title="Avg Wellness"
                    value={trendsData.wellness?.average_score || 0}
                    suffix="/10"
                    change={trendsData.wellness?.change}
                    icon={<Heart className="w-5 h-5" />}
                    color="purple"
                  />
                  <SummaryCard
                    title="Health Checks"
                    value={trendsData.wellness?.data_points || 0}
                    icon={<Activity className="w-5 h-5" />}
                    color="blue"
                  />
                  <SummaryCard
                    title="Metrics Tracked"
                    value={trendsData.metrics?.types_tracked?.length || 0}
                    icon={<Scale className="w-5 h-5" />}
                    color="green"
                  />
                  <SummaryCard
                    title="Symptoms Logged"
                    value={trendsData.symptoms?.total_logged || 0}
                    icon={<Thermometer className="w-5 h-5" />}
                    color="amber"
                  />
                </div>

                {/* Wellness Line Chart */}
                {trendsData.wellness?.trend_data?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Wellness Score Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trendsData.wellness.trend_data}>
                        <defs>
                          <linearGradient id="wellnessGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                        <YAxis domain={[0, 10]} stroke="#9ca3af" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="score" 
                          stroke={COLORS.primary} 
                          fill="url(#wellnessGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Symptom Distribution Pie Chart */}
                {trendsData.symptoms?.severity_distribution && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-slate-800/50 rounded-2xl p-6">
                      <h3 className="text-lg font-bold text-white mb-4">Symptom Severity</h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Mild', value: trendsData.symptoms.severity_distribution.mild },
                              { name: 'Moderate', value: trendsData.symptoms.severity_distribution.moderate },
                              { name: 'Severe', value: trendsData.symptoms.severity_distribution.severe }
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill={COLORS.mild} />
                            <Cell fill={COLORS.moderate} />
                            <Cell fill={COLORS.severe} />
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Top Symptoms */}
                    <div className="bg-slate-800/50 rounded-2xl p-6">
                      <h3 className="text-lg font-bold text-white mb-4">Top Symptoms</h3>
                      {trendsData.symptoms.top_symptoms?.length > 0 ? (
                        <div className="space-y-3">
                          {trendsData.symptoms.top_symptoms.map((symptom, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-gray-300">{symptom.name}</span>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="h-2 rounded-full bg-purple-500"
                                  style={{ width: `${Math.min(symptom.count * 20, 100)}px` }}
                                />
                                <span className="text-gray-400 text-sm w-8">{symptom.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400">No symptoms logged</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Wellness Tab */}
            {activeTab === 'wellness' && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Wellness Score History</h3>
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                      trendsData.wellness?.trend === 'improving' ? 'bg-green-500/20 text-green-400' :
                      trendsData.wellness?.trend === 'declining' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {trendsData.wellness?.trend === 'improving' ? <TrendingUp className="w-4 h-4" /> :
                       trendsData.wellness?.trend === 'declining' ? <TrendingDown className="w-4 h-4" /> :
                       <Minus className="w-4 h-4" />}
                      {trendsData.wellness?.trend || 'stable'}
                    </div>
                  </div>
                  
                  {trendsData.wellness?.trend_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={trendsData.wellness.trend_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                        <YAxis domain={[0, 10]} stroke="#9ca3af" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="score" 
                          stroke={COLORS.primary}
                          strokeWidth={3}
                          dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No wellness data yet</p>
                      <p className="text-sm">Complete a Quick Health Check to start tracking</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metrics Tab */}
            {activeTab === 'metrics' && (
              <div className="space-y-6">
                {Object.keys(trendsData.metrics?.trend_data || {}).length > 0 ? (
                  Object.entries(trendsData.metrics.trend_data).map(([metricType, data]) => (
                    <div key={metricType} className="bg-slate-800/50 rounded-2xl p-6">
                      <h3 className="text-lg font-bold text-white mb-4 capitalize">
                        {metricType.replace(/_/g, ' ')}
                        {data.length > 0 && (
                          <span className="text-gray-400 text-sm font-normal ml-2">
                            Latest: {data[data.length-1]?.raw_value} {data[data.length-1]?.unit}
                          </span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                          <YAxis stroke="#9ca3af" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                            labelStyle={{ color: '#fff' }}
                            formatter={(value, name, props) => [props.payload.raw_value + ' ' + props.payload.unit, 'Value']}
                          />
                          <Bar dataKey="value" fill={COLORS.info} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-400 bg-slate-800/50 rounded-2xl">
                    <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No metrics logged yet</p>
                    <p className="text-sm">Start logging metrics like weight, blood pressure, sleep hours</p>
                  </div>
                )}
              </div>
            )}

            {/* Symptoms Tab */}
            {activeTab === 'symptoms' && (
              <div className="space-y-6">
                {/* Daily Symptom Count */}
                {trendsData.symptoms?.daily_symptom_counts?.length > 0 && (
                  <div className="bg-slate-800/50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Daily Symptom Count</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={trendsData.symptoms.daily_symptom_counts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                        <YAxis stroke="#9ca3af" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="count" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Category Distribution */}
                {Object.keys(trendsData.symptoms?.category_distribution || {}).length > 0 && (
                  <div className="bg-slate-800/50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Symptoms by Category</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(trendsData.symptoms.category_distribution).map(([name, value]) => ({
                            name: name.replace(/_/g, ' '),
                            value
                          }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {Object.keys(trendsData.symptoms.category_distribution).map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {!trendsData.symptoms?.daily_symptom_counts?.length && 
                 !Object.keys(trendsData.symptoms?.category_distribution || {}).length && (
                  <div className="text-center py-12 text-gray-400 bg-slate-800/50 rounded-2xl">
                    <Thermometer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No symptoms logged yet</p>
                    <p className="text-sm">Track your symptoms to see patterns over time</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, suffix = '', change, icon, color }) => {
  const colorClasses = {
    purple: 'from-purple-600/20 to-purple-600/5 border-purple-500/30 text-purple-400',
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-500/30 text-blue-400',
    green: 'from-green-600/20 to-green-600/5 border-green-500/30 text-green-400',
    amber: 'from-amber-600/20 to-amber-600/5 border-amber-500/30 text-amber-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 border`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{title}</span>
        {icon}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-white">{value}</span>
        {suffix && <span className="text-gray-400">{suffix}</span>}
      </div>
      {change !== undefined && change !== 0 && (
        <div className={`flex items-center gap-1 mt-1 text-xs ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {Math.abs(change).toFixed(1)} from last period
        </div>
      )}
    </div>
  );
};

export default HealthTrendsDashboard;
